import numeral from "numeral";
import dayjs from "dayjs";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import { PngExportService } from "app/services/PngExportService";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import { AdsTrafficTooltip } from "app/pages/website-analysis/analyze-publishers/common/ads-traffic/AdsTrafficTooltip";

/**
 * Formats the given number as a human-readable string for displaying on the chart's y-axis.
 */
const formatTrafficAxis = ({ value }: { value: number }) => numeral(value).format("0,0");

/**
 * Formats the given traffic value to a human-readable string.
 */
const formatTrafficTooltip = (value: number) => numeral(value).format("0,0");

/**
 * Custom tooltip for the chart. shows the current traffic on the active date.
 */
const buildTooltipConfig = () => {
    return {
        followPointer: false,
        shared: true,
        useHTML: true,
        ...tooltipContainerConfig().tooltip,
        formatter: function () {
            return AdsTrafficTooltip({
                metricName: i18nFilter()("analysis.publishers.ads.traffic.graph.tooltip.share"),
                points: this.points,
                timeGranularityName: "Monthly",
                yTooltipFormatter: formatTrafficTooltip, // const yTooltipFormatter = (value) => numberFilter()(value, 2);
            });
        },
    };
};

/**
 * Builds the base configuartion object for the paid-outgoing traffic chart
 */
const buildBaseChartConfig = () => {
    const chartConfig = {
        chart: {
            height: 212,
            spacingTop: 10,
            plotBackgroundColor: "transparent",
            style: {
                fontFamily: "Roboto",
            },
            events: {},
            animation: false,
        },
        yAxis: {
            showFirstLabel: false,
            showLastLabel: true,
        },
        tooltip: buildTooltipConfig(),
        plotOptions: {
            line: {
                marker: {
                    enabled: true,
                    symbol: "circle",
                },
                lineWidth: 2,
                connectNulls: true,
            },
            series: {
                fillOpacity: 1,
                animation: false,
            },
        },
        exporting: PngExportService.getInstance().getSettings({
            chartOptions: {
                chart: {
                    marginLeft: 200,
                },
                yAxis: {
                    labels: {
                        formatter: function () {
                            return formatTrafficAxis(this);
                        },
                    },
                },
                title: {
                    text: null,
                    style: {
                        display: "block",
                    },
                },
                legend: {
                    enabled: true,
                },
            },
        }),
    };

    return chartConfig;
};

export const getChartConfig = () => {
    const baseConfig = buildBaseChartConfig();
    const yAxisFormatter = ({ value }) => abbrNumberFilter()(value);
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format("MMM YY");

    const chartConfig = combineConfigs(
        {
            yAxisFormatter,
            xAxisFormatter,
        },
        [
            baseConfig,
            xAxisLabelsConfig, // Used in combination with our custom xAxisFormatter
            yAxisLabelsConfig, // Used in combination with our custom yAxisFormatter
            xAxisCrosshair,
            monthlyIntervalConfig, // Used to properly format the date labels on the xAxis
            noLegendConfig,
        ],
    );
    return chartConfig;
};
